import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { SiteData, Position, Certification } from "../../types";
import { useTimelineScales } from "../../hooks/useTimelineScales";
import {
  AXIS_HEIGHT,
  INITIAL_SCROLL_ANCHOR,
  LANE_GAP,
  LANE_HEIGHT,
  PARALLAX_FACTOR,
  SECTION_GAP,
} from "./constants";
import { parseDateString } from "../../lib/dates";
import { XAxis } from "./XAxis";
import { PositionBar } from "./PositionBar";
import { CertificationMarker } from "./CertificationMarker";
import { GenericBar } from "./GenericBar";
import { Tooltip, type TooltipContent } from "./Tooltip";

interface TimelineProps {
  data: SiteData;
}

export interface TimelineHandle {
  jumpTo: (yearMonth: string) => void;
}

export const Timeline = forwardRef<TimelineHandle, TimelineProps>(function Timeline(
  { data },
  ref,
) {
  const scales = useTimelineScales(data);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [parallaxY, setParallaxY] = useState(0);
  const [tooltip, setTooltip] = useState<{
    content: TooltipContent | null;
    anchor: { x: number; y: number } | null;
  }>({ content: null, anchor: null });

  // Section layout — compute Y offsets for each section
  const positionsHeight = data.lanes.positionLaneCount * (LANE_HEIGHT + LANE_GAP);
  const educationHeight = data.lanes.educationLaneCount * (LANE_HEIGHT + LANE_GAP);
  const projectsHeight = data.lanes.projectLaneCount * (LANE_HEIGHT + LANE_GAP);
  const certHeight = LANE_HEIGHT;

  const positionsY = AXIS_HEIGHT + 20;
  const certificationsY = positionsY + positionsHeight + SECTION_GAP;
  const educationY = certificationsY + certHeight + SECTION_GAP;
  const projectsY = educationY + educationHeight + SECTION_GAP;
  const totalHeight = projectsY + projectsHeight + 40;

  useImperativeHandle(ref, () => ({
    jumpTo(yearMonth: string) {
      if (!scrollRef.current) return;
      const anchor =
        yearMonth === "now" ? new Date() : parseDateString(yearMonth);
      if (!anchor) return;
      const x = scales.xScale(anchor);
      scrollRef.current.scrollTo({
        left: Math.max(0, x - 80),
        behavior: "smooth",
      });
    },
  }));

  // Anchor scroll to January 2024 on mount.
  // Use rAF to ensure the SVG has been measured before we set scrollLeft —
  // some browsers don't update scrollWidth synchronously after a width attr
  // change on an inline SVG.
  useLayoutEffect(() => {
    const anchor = parseDateString(INITIAL_SCROLL_ANCHOR);
    if (!anchor || !scrollRef.current) return;
    const x = scales.xScale(anchor);
    const target = Math.max(0, x - 80);
    const apply = () => {
      if (scrollRef.current) scrollRef.current.scrollLeft = target;
    };
    apply();
    requestAnimationFrame(apply);
  }, [scales]);

  // Map vertical mouse-wheel deltas to horizontal scrolling.
  // The timeline has no vertical scroll, so wheel-Y would otherwise be wasted.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      // Don't hijack pinch-zoom (ctrlKey is set on trackpad pinch)
      if (e.ctrlKey) return;
      // If the user is scrolling primarily vertically, redirect to horizontal.
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // Track scroll for parallax Y-drift
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const anchor = parseDateString(INITIAL_SCROLL_ANCHOR);
    const anchorX = anchor ? scales.xScale(anchor) - 80 : 0;
    const handler = () => {
      const drift = (anchorX - el.scrollLeft) * PARALLAX_FACTOR;
      setParallaxY(drift);
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, [scales]);

  const handlePositionHover = (
    position: Position | null,
    anchor: { x: number; y: number } | null,
  ) => {
    if (position && anchor) {
      setTooltip({
        content: { kind: "position", position },
        anchor,
      });
    } else {
      setTooltip({ content: null, anchor: null });
    }
  };

  const handleCertHover = (
    certification: Certification | null,
    anchor: { x: number; y: number } | null,
  ) => {
    if (certification && anchor) {
      setTooltip({
        content: { kind: "certification", certification },
        anchor,
      });
    } else {
      setTooltip({ content: null, anchor: null });
    }
  };

  const handleEducationHover = (id: string | null, anchor: { x: number; y: number } | null) => {
    if (!id || !anchor) {
      setTooltip({ content: null, anchor: null });
      return;
    }
    const e = data.education.find((x) => x.id === id);
    if (!e) return;
    setTooltip({
      content: {
        kind: "text",
        title: e.degree ? `${e.degree} · ${e.school}` : e.school,
        subtitle: `${e.startDate}${e.endDate ? ` – ${e.endDate}` : ""}`,
        body: e.notes ?? e.activities ?? undefined,
      },
      anchor,
    });
  };

  const handleProjectHover = (id: string | null, anchor: { x: number; y: number } | null) => {
    if (!id || !anchor) {
      setTooltip({ content: null, anchor: null });
      return;
    }
    const p = data.projects.find((x) => x.id === id);
    if (!p) return;
    setTooltip({
      content: {
        kind: "text",
        title: p.title,
        subtitle: `${p.startDate}${p.endDate ? ` – ${p.endDate}` : ""}`,
        body: p.description,
      },
      anchor,
    });
  };

  return (
    <>
      <div className="relative h-full w-full">
        {/* Sticky left-side section labels — overlay outside the scroll surface,
            but with the same parallax Y so they track the lanes. */}
        <div
          className="absolute top-0 left-0 z-10 pointer-events-none"
          style={{
            height: totalHeight,
            transform: `translateY(${parallaxY}px)`,
          }}
        >
          <SectionLabelDiv y={positionsY - 8} label="Positions" count={data.positions.length} />
          <SectionLabelDiv
            y={certificationsY - 8}
            label="Certifications"
            count={data.certifications.length}
          />
          <SectionLabelDiv y={educationY - 8} label="Education" count={data.education.length} />
          <SectionLabelDiv y={projectsY - 8} label="Projects" count={data.projects.length} />
        </div>

      <div
        ref={scrollRef}
        className="timeline-scroll h-full w-full overflow-x-auto overflow-y-auto"
        style={{ scrollBehavior: "auto" }}
      >
        <svg
          width={scales.totalWidth}
          height={totalHeight}
          style={{ display: "block" }}
        >
          <g transform={`translate(0, ${parallaxY})`}>
            <XAxis scales={scales} height={totalHeight} />

            {/* Section dividers — hairline rules between sections */}
            <SectionDivider y={certificationsY - 16} width={scales.totalWidth} />
            <SectionDivider y={educationY - 16} width={scales.totalWidth} />
            <SectionDivider y={projectsY - 16} width={scales.totalWidth} />

            {/* Positions */}
            {data.positions.map((p) => (
              <PositionBar
                key={p.id}
                position={p}
                scales={scales}
                laneOffsetY={positionsY}
                onHover={handlePositionHover}
              />
            ))}

            {/* Certifications */}
            {data.certifications.map((c) => (
              <CertificationMarker
                key={c.id}
                certification={c}
                scales={scales}
                y={certificationsY + LANE_HEIGHT / 2}
                onHover={handleCertHover}
              />
            ))}

            {/* Education */}
            {data.education.map((e) => (
              <GenericBar
                key={e.id}
                id={e.id}
                startDate={e.startDate}
                endDate={e.endDate}
                subLane={e.subLane}
                label={e.school}
                scales={scales}
                laneOffsetY={educationY}
                color="var(--color-employer-3)"
                onHover={handleEducationHover}
              />
            ))}

            {/* Projects */}
            {data.projects.map((p) => (
              <GenericBar
                key={p.id}
                id={p.id}
                startDate={p.startDate}
                endDate={p.endDate}
                subLane={p.subLane}
                label={p.title}
                scales={scales}
                laneOffsetY={projectsY}
                color="var(--color-employer-7)"
                onHover={handleProjectHover}
              />
            ))}
          </g>
        </svg>
      </div>

      </div>
      <Tooltip content={tooltip.content} anchor={tooltip.anchor} />
    </>
  );
});

function SectionLabelDiv({ y, label, count }: { y: number; label: string; count: number }) {
  return (
    <div
      className="absolute select-none"
      style={{ top: y, left: 0 }}
    >
      <div
        className="flex items-baseline gap-2 px-5 py-2"
        style={{
          background:
            "linear-gradient(to right, rgba(236, 236, 236, 0.96) 60%, rgba(236, 236, 236, 0))",
          backdropFilter: "blur(6px)",
        }}
      >
        <span
          className="display-italic text-[15px] leading-none"
          style={{ color: "var(--color-cream)", fontWeight: 400 }}
        >
          {label}
        </span>
        <span
          className="mono text-[10px]"
          style={{ color: "var(--color-cream-dim)", letterSpacing: "0.15em" }}
        >
          ({String(count).padStart(2, "0")})
        </span>
      </div>
    </div>
  );
}

function SectionDivider({ y, width }: { y: number; width: number }) {
  return (
    <line
      x1={0}
      x2={width}
      y1={y}
      y2={y}
      stroke="var(--color-rule)"
      strokeWidth={0.5}
      opacity={0.6}
    />
  );
}

